import React, { useState, useRef, useEffect } from "react";
import "./SchoolLetterHeader.css";

import type { 
  CanvasElement, 
  TextElement, 
  ImageElement, 
  ElementPatch, 
  Align 
} from "./SchoolLetterHeader";

import { 
  PAGE_W, 
  PAGE_H, 
  SNAP, 
  FONT_SIZES,
  SCHOOL 
} from "./SchoolLetterHeader";

interface LetterEditorProps {
  elements: CanvasElement[];
  selId: string | null;
  zoom: number;
  letterheadTitle: string;
  printing: boolean;
  onTitleChange: (title: string) => void;
  onAddEl: (o?: Partial<TextElement>) => void;
  onAddImgEl: (o?: Partial<ImageElement>) => void;
  onUpdateEl: (id: string, patch: ElementPatch) => void;
  onDeleteEl: (id: string) => void;
  onDuplicateEl: (id: string) => void;
  onSaveAndPrint: () => void;
  onSetSelId: (id: string | null) => void;
  SCHOOL: typeof SCHOOL;
  PAGE_W: number;
  PAGE_H: number;
  SNAP: number;
  FONT_SIZES: number[];
  template?: any;
  onOpenTemplateSetup?: () => void;
}

const EditableText: React.FC<{
  el: TextElement;
  isSelected: boolean;
  onUpdate: (id: string, patch: ElementPatch) => void;
  savedSelRef: React.MutableRefObject<Range | null>;
}> = ({ el, isSelected, onUpdate, savedSelRef }) => {
  const divRef = useRef<HTMLDivElement>(null);

  const lastIdRef = useRef<string>("");
  useEffect(() => {
    if (!divRef.current) return;
    if (lastIdRef.current === el.id) return;
    lastIdRef.current = el.id;
    divRef.current.innerHTML = el.html || el.content;
  }, [el.id, el.html, el.content]);

  return (
    <div
      ref={divRef}
      className="el-text"
      contentEditable
      suppressContentEditableWarning
      style={{
        fontSize: el.fontSize,
        fontFamily: el.fontFamily,
        color: el.color,
        fontWeight: el.bold ? "bold" : "normal",
        fontStyle: el.italic ? "italic" : "normal",
        textDecoration: el.underline ? "underline" : "none",
        textAlign: el.align,
      }}
      onSelect={() => {
        const sel = window.getSelection();
        if (sel && sel.rangeCount > 0) {
          savedSelRef.current = sel.getRangeAt(0).cloneRange();
        }
      }}
      onInput={(e) => {
        const target = e.target as HTMLDivElement;
        onUpdate(el.id, {
          html: target.innerHTML,
          content: target.innerText,
        });
      }}
    />
  );
};

const LetterEditor: React.FC<LetterEditorProps> = ({
  elements,
  selId,
  zoom,
  letterheadTitle,
  printing,
  onTitleChange,
  onAddEl,
  onAddImgEl,
  onUpdateEl,
  onDeleteEl,
  onDuplicateEl,
  onSaveAndPrint,
  onSetSelId,
  SCHOOL,
  PAGE_W,
  PAGE_H,
  SNAP,
  FONT_SIZES,
  template,
  onOpenTemplateSetup
}) => {
  const sel = elements.find((e) => e.id === selId) ?? null;
  const pageRef = useRef<HTMLDivElement>(null);
  const headerImgRef = useRef<HTMLImageElement>(null);
  const footerImgRef = useRef<HTMLImageElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const savedSelRef = useRef<Range | null>(null);
  const sigFileRef = useRef<HTMLInputElement>(null);
  const dragRef = useRef<any>(null);
  const resizeRef = useRef<any>(null);

  const patchSel = (p: ElementPatch) => {
    if (selId) onUpdateEl(selId, p);
  };

  const execOnSel = (cmd: string) => {
    if (savedSelRef.current) {
      const s = window.getSelection()!;
      s.removeAllRanges();
      s.addRange(savedSelRef.current);
    }
    document.execCommand(cmd, false, undefined);
    const f = document.activeElement as HTMLElement | null;
    if (f?.classList.contains("el-text") && selId) {
      onUpdateEl(selId, { html: (f as HTMLDivElement).innerHTML, content: (f as HTMLDivElement).innerText });
    }
  };

  const handleSigFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      if (selId && sel?.type === "image") onUpdateEl(selId, { src: ev.target!.result as string });
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const addSignOff = () => {
    const textEl: Partial<TextElement> = {
      content: "Yours sincerely,\n\n\n\n________________________\nClass Teacher",
      html: "Yours sincerely,\n\n\n\n________________________\nClass Teacher",
      y: 680,
      fontSize: 12,
    };
    const imgEl: Partial<ImageElement> = { x: 80, y: 710, width: 180, height: 70 };
    onAddEl(textEl);
    onAddImgEl(imgEl);
  };

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!pageRef.current) return;
      const rect = pageRef.current.getBoundingClientRect();
      if (dragRef.current) {
        const { id, offX, offY, zoom: z } = dragRef.current;
        const newX = Math.round(Math.max(0, Math.min((e.clientX - rect.left) / z - offX, PAGE_W - 40)));
        const newY = Math.round(Math.max(0, Math.min((e.clientY - rect.top) / z - offY, PAGE_H - 20)));
        const d = pageRef.current.querySelector<HTMLElement>(`[data-id="${id}"]`);
        if (d) {
          d.style.left = `${newX}px`;
          d.style.top = `${newY}px`;
        }
        dragRef.current.lastX = newX;
        dragRef.current.lastY = newY;
      }
      if (resizeRef.current) {
        const { id, startX, startY, origW, origH, zoom: z, isImg } = resizeRef.current;
        const newW = Math.max(40, origW + (e.clientX - startX) / z);
        const d = pageRef.current.querySelector<HTMLElement>(`[data-id="${id}"]`);
        if (d) d.style.width = `${newW}px`;
        if (isImg) {
          const newH = Math.max(20, origH + (e.clientY - startY) / z);
          if (d) d.style.height = `${newH}px`;
          resizeRef.current.lastH = newH;
        }
        resizeRef.current.lastW = newW;
      }
    };

    const onUp = () => {
      if (dragRef.current) {
        const { id, lastX, lastY } = dragRef.current;
        if (lastX !== undefined) onUpdateEl(id, { x: lastX, y: lastY });
        dragRef.current = null;
      }
      if (resizeRef.current) {
        const { id, lastW, lastH, isImg } = resizeRef.current;
        if (lastW !== undefined) {
          const p: ElementPatch = { width: lastW };
          if (isImg && lastH !== undefined) (p as Partial<ImageElement>).height = lastH;
          onUpdateEl(id, p);
        }
        resizeRef.current = null;
      }
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [onUpdateEl]);

  return (
    <div className="slh">
      <div className="slh-bar">
        <span className="slh-bar-title">
          ✦ Letter Composer — {SCHOOL.name}
        </span>
        
        <div className="slh-sep" />

        <input
          type="text"
          value={letterheadTitle}
          onChange={(e) => onTitleChange(e.target.value)}
          placeholder="Letterhead Title"
          className="prop-inp"
          style={{ width: 220, height: 27, margin: "0 8px", fontSize: 12 }}
        />
        <button className="tb" onClick={() => onAddEl({ content: "Dear Parents / Carers,", html: "Dear Parents / Carers,", y: 220, fontSize: 13 })}>
          <i className="pi pi-plus" style={{ fontSize: 11 }} /> Salutation
        </button>
        <button className="tb" onClick={() => onAddEl({ content: "Re: ", html: "<b>Re: </b>", y: 195, fontSize: 13 })}>
          <i className="pi pi-plus" style={{ fontSize: 11 }} /> Subject
        </button>
        <button className="tb" onClick={() => onAddEl({ content: new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }), y: 175, x: 500, width: 255, align: "right", fontSize: 12 })}>
          <i className="pi pi-plus" style={{ fontSize: 11 }} /> Date
        </button>
        <button className="tb" onClick={() => onAddEl({ content: "Write your paragraph here...", y: 260, width: 620, fontSize: 13 })}>
          <i className="pi pi-plus" style={{ fontSize: 11 }} /> Paragraph
        </button>
        <button className="tb" style={{ borderColor: "#6ee7b7", color: "#065f46", background: "#f0fdf4" }} onClick={addSignOff}>
          <i className="pi pi-pencil" style={{ fontSize: 11 }} /> Sign-off + Signature
        </button>
        <div className="slh-sep" />
        <select
          className="tb-sel"
          disabled={!sel || sel.type === "image"}
          value={(sel as TextElement | null)?.fontFamily ?? ""}
          onChange={(e) => patchSel({ fontFamily: e.target.value })}
        >
          <option value="'DM Sans',sans-serif">DM Sans</option>
          <option value="'Playfair Display',serif">Playfair Display</option>
          <option value="'Lato',sans-serif">Lato</option>
          <option value="'Merriweather',serif">Merriweather</option>
          <option value="Georgia,serif">Georgia</option>
          <option value="'Courier New',monospace">Courier New</option>
        </select>
        <select
          className="tb-sel"
          style={{ width: 50 }}
          disabled={!sel || sel.type === "image"}
          value={(sel as TextElement | null)?.fontSize ?? 13}
          onChange={(e) => patchSel({ fontSize: +e.target.value })}
        >
          {FONT_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <button className={`tb${(sel as TextElement | null)?.bold ? " on" : ""}`} disabled={!sel || sel.type === "image"} onClick={() => patchSel({ bold: !(sel as TextElement).bold })}>
          <i className="pi pi-bold" style={{ fontSize: 12 }} />
        </button>
        <button className={`tb${(sel as TextElement | null)?.italic ? " on" : ""}`} disabled={!sel || sel.type === "image"} onClick={() => patchSel({ italic: !(sel as TextElement).italic })}>
          <i className="pi pi-italic" style={{ fontSize: 12 }} />
        </button>
        <button className={`tb${(sel as TextElement | null)?.underline ? " on" : ""}`} disabled={!sel || sel.type === "image"} onClick={() => patchSel({ underline: !(sel as TextElement).underline })}>
          <i className="pi pi-underline" style={{ fontSize: 12 }} />
        </button>
        <div className="slh-sep" />
        <span style={{ fontSize: 10, color: "#9ca3af", marginLeft: 2, marginRight: 2, whiteSpace: "nowrap" }}>Selection:</span>
        <button className="tb" disabled={!sel || sel.type === "image"} onClick={() => execOnSel("bold")} style={{ fontWeight: 700, fontSize: 12 }}>B</button>
        <button className="tb" disabled={!sel || sel.type === "image"} onClick={() => execOnSel("italic")} style={{ fontStyle: "italic", fontSize: 12 }}>I</button>
        <button className="tb" disabled={!sel || sel.type === "image"} onClick={() => execOnSel("underline")} style={{ textDecoration: "underline", fontSize: 12 }}>U</button>
        <div className="slh-sep" />
        <button className={`tb${(sel as TextElement | null)?.align === "left" ? " on" : ""}`} disabled={!sel || sel.type === "image"} onClick={() => patchSel({ align: "left" })}>
          <i className="pi pi-align-left" style={{ fontSize: 12 }} />
        </button>
        <button className={`tb${(sel as TextElement | null)?.align === "center" ? " on" : ""}`} disabled={!sel || sel.type === "image"} onClick={() => patchSel({ align: "center" })}>
          <i className="pi pi-align-center" style={{ fontSize: 12 }} />
        </button>
        <button className={`tb${(sel as TextElement | null)?.align === "right" ? " on" : ""}`} disabled={!sel || sel.type === "image"} onClick={() => patchSel({ align: "right" })}>
          <i className="pi pi-align-right" style={{ fontSize: 12 }} />
        </button>
        <div className="slh-sep" />
        <button className="tb" disabled={!sel} onClick={() => { if (sel) patchSel({ x: Math.round((PAGE_W - sel.width) / 2) }); }}>
          <i className="pi pi-align-center" style={{ fontSize: 12 }} /> Centre
        </button>
        <div className="slh-sep" />
        <input type="color" className="tb-color" disabled={!sel || sel.type === "image"} value={(sel as TextElement | null)?.color ?? "#1e3a8a"} onChange={(e) => patchSel({ color: e.target.value })} />
        <div className="slh-sep" />
        <button className="tb red" disabled={!sel} onClick={() => { if (sel) onDeleteEl(sel.id); }}>
          <i className="pi pi-trash" style={{ fontSize: 12 }} /> Delete
        </button>
        <div style={{ flex: 1 }} />
        <div style={{ display: "flex", alignItems: "center", gap: 1, marginRight: 6, background: "#f8faff", border: "1px solid #e5e7eb", borderRadius: 7, padding: "2px 4px" }}>
          <button className="tb" style={{ height: 23, width: 23, padding: 0, justifyContent: "center" }}><i className="pi pi-search-minus" style={{ fontSize: 12 }} /></button>
          <button style={{ height: 23, minWidth: 40, padding: "0 4px", border: "none", background: "transparent", cursor: "pointer", fontSize: 11, fontWeight: 700, color: "#1e3a8a", fontFamily: "'DM Sans',sans-serif", borderRadius: 4 }}>{Math.round(zoom * 100)}%</button>
          <button className="tb" style={{ height: 23, width: 23, padding: 0, justifyContent: "center" }}><i className="pi pi-search-plus" style={{ fontSize: 12 }} /></button>
        </div>
        <div className="slh-sep" />
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button 
            className="tb" 
            onClick={onOpenTemplateSetup}
            style={{ 
              background: "#f3e8ff", 
              color: "#6b21a8", 
              borderColor: "#c4b5fd" 
            }}
          >
            <i className="pi pi-cog" style={{ fontSize: 13 }} /> Template Setup
          </button>

          <button className="btn-print" disabled={printing} onClick={onSaveAndPrint}>
            {printing ? (
              <><i className="pi pi-spinner pi-spin" style={{ fontSize: 13 }}></i> Saving & Printing…</>
            ) : (
              <><i className="pi pi-print" style={{ fontSize: 13, color: "white" }} /> Save & Print</>
            )}
          </button>
        </div>
      </div>

      <div className="slh-body">
        <div className="slh-left">
          <div className="panel-head">
            <h3>Elements</h3>
            <p>Hover → grab bar to drag</p>
          </div>
          <div className="fmt-hint">
            💡 Select text → B/I/U toolbar pops up<br />
            🖊 Signature slot → click to upload image
          </div>
          <div className="el-list">
            {elements.length === 0 && (
              <div style={{ padding: "18px 8px", textAlign: "center", color: "#9ca3af", fontSize: 11, lineHeight: 1.7 }}>
                No elements yet.<br />Use toolbar to add.
              </div>
            )}
            {elements.map((el) => (
              <div
                key={el.id}
                className={`el-item${el.type === "image" ? " img-el" : ""}${el.id === selId ? " sel" : ""}`}
                onClick={() => onSetSelId(el.id)}
                style={{ display: "flex", alignItems: "flex-start", gap: 4 }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="el-name" style={{ display: "flex", alignItems: "center", gap: 4, color: el.type === "image" ? "#065f46" : "#374151" }}>
                    {el.type === "image" && <i className="pi pi-image" style={{ fontSize: 10, color: "#10b981" }} />}
                    {el.type === "image"
                      ? (el as ImageElement).src ? "✓ Signature" : "⬜ Signature (empty)"
                      : el.content.substring(0, 22) + (el.content.length > 22 ? "…" : "")}
                  </div>
                  <div className="el-meta">x:{Math.round(el.x)} y:{Math.round(el.y)}</div>
                </div>
                <button
                  title="Delete"
                  onClick={(e) => { e.stopPropagation(); onDeleteEl(el.id); }}
                  style={{ flexShrink: 0, width: 18, height: 18, borderRadius: 4, border: "none", background: "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#9ca3af", padding: 0, marginTop: 1 }}
                >
                  <i className="pi pi-trash" style={{ fontSize: 11 }} />
                </button>
              </div>
            ))}
          </div>
          <button className="add-btn" onClick={() => onAddEl()}> <i className="pi pi-plus" style={{ fontSize: 11 }} /> Add text block</button>
          <button className="add-btn green" onClick={() => onAddImgEl({ x: 80, y: 700, width: 180, height: 70 })}> <i className="pi pi-image" style={{ fontSize: 11 }} /> Add signature slot</button>
        </div>

        <div ref={canvasRef} className="slh-canvas">
          <div style={{ width: PAGE_W * zoom, height: PAGE_H * zoom, flexShrink: 0, marginBottom: 20 }}>
            <div style={{ transform: `scale(${zoom})`, transformOrigin: "top left", width: PAGE_W, height: PAGE_H }}>
              <div
                ref={pageRef}
                className="slh-page"
                style={{ width: PAGE_W, height: PAGE_H }}
                onMouseDown={(e) => { if (e.target === pageRef.current) onSetSelId(null); }}
              >
                <img
                  ref={headerImgRef}
                  className="pg-header"
                  src={SCHOOL.headerImage}
                  alt="header"
                  draggable={false}
                />
                <img
                  ref={footerImgRef}
                  className="pg-footer"
                  src={SCHOOL.footerImage}
                  alt="footer"
                  draggable={false}
                />
                <div className="pg-layer">
                  {elements.map((el) => (
                    <div
                      key={el.id}
                      data-id={el.id}
                      className={`slh-el${el.type === "image" ? " img-el" : ""}${el.id === selId ? " sel" : ""}`}
                      style={{ 
                        left: el.x, 
                        top: el.y, 
                        width: el.width, 
                        height: el.type === "image" ? (el as ImageElement).height : undefined, 
                        position: "absolute" 
                      }}
                      onClick={(e) => { e.stopPropagation(); onSetSelId(el.id); }}
                    >
                      <div
                        className={`el-handle${el.type === "image" ? " green" : ""}`}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onSetSelId(el.id);
                          const rect = pageRef.current!.getBoundingClientRect();
                          dragRef.current = { 
                            id: el.id, 
                            offX: (e.clientX - rect.left) / zoom - el.x, 
                            offY: (e.clientY - rect.top) / zoom - el.y, 
                            zoom 
                          };
                        }}
                      >
                        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                          <i className="pi pi-arrows-alt" style={{ fontSize: 9, color: "rgba(255,255,255,0.8)" }} />
                          <span className="el-handle-label">
                            {el.type === "image" ? "✦ Signature image" : el.content.substring(0, 22)}
                          </span>
                        </span>
                        <button className="el-handle-del" onMouseDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); onDeleteEl(el.id); }}>✕</button>
                      </div>
                      <div className="el-box">
                        {el.type === "image" ? (
                          <div className="sig-box" onClick={() => sigFileRef.current?.click()}>
                            {(el as ImageElement).src ? (
                              <img className="sig-img" src={(el as ImageElement).src!} alt="signature" draggable={false} />
                            ) : (
                              <div className="sig-drop">Click or drop signature image</div>
                            )}
                          </div>
                        ) : (
                          <EditableText
                            key={el.id}
                            el={el as TextElement}
                            isSelected={el.id === selId}
                            onUpdate={onUpdateEl}
                            savedSelRef={savedSelRef}
                          />
                        )}
                        {el.id === selId && (
                          el.type === "image" ? (
                            <div
                              className="el-resize-img"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                resizeRef.current = { 
                                  id: el.id, 
                                  startX: e.clientX, 
                                  startY: e.clientY, 
                                  origW: el.width, 
                                  origH: (el as ImageElement).height, 
                                  zoom, 
                                  isImg: true 
                                };
                              }}
                            />
                          ) : (
                            <div
                              className="el-resize"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                resizeRef.current = { 
                                  id: el.id, 
                                  startX: e.clientX, 
                                  startY: e.clientY, 
                                  origW: el.width, 
                                  origH: 0, 
                                  zoom, 
                                  isImg: false 
                                };
                              }}
                            />
                          )
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="slh-right">
          {!sel ? (
            <div className="no-sel">Select an element to see its properties here.</div>
          ) : sel.type === "image" ? (
            <div className="props">
              <div className="prop-sec green">Signature</div>
              <div className="sig-panel">
                <div className="sig-panel-title">
                  <i className="pi pi-image" style={{ fontSize: 11, color: "#065f46" }} /> Upload signature image
                </div>
                {(sel as ImageElement).src ? (
                  <>
                    <img className="sig-thumb" src={(sel as ImageElement).src!} alt="signature preview" />
                    <button className="sig-upload-btn" onClick={() => sigFileRef.current?.click()}>Replace image</button>
                    <button className="sig-clear-btn" onClick={() => onUpdateEl(sel.id, { src: null })}>Remove</button>
                  </>
                ) : (
                  <button className="sig-upload-btn" onClick={() => sigFileRef.current?.click()}>Choose file…</button>
                )}
              </div>
              <input ref={sigFileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleSigFile} />
              <div className="prop-sec green">Position &amp; Size</div>
              <div className="prop-lbl">X (px)</div>
              <input className="prop-inp" type="number" value={Math.round(sel.x)} onChange={(e) => patchSel({ x: Math.round(+e.target.value) })} />
              <div className="prop-lbl">Y (px)</div>
              <input className="prop-inp" type="number" value={Math.round(sel.y)} onChange={(e) => patchSel({ y: Math.round(+e.target.value) })} />
              <div className="prop-lbl">Width (px)</div>
              <input className="prop-inp" type="number" value={Math.round(sel.width)} onChange={(e) => patchSel({ width: Math.max(40, Math.round(+e.target.value)) })} />
              <div className="prop-lbl">Height (px)</div>
              <input className="prop-inp" type="number" value={Math.round((sel as ImageElement).height)} onChange={(e) => patchSel({ height: Math.max(20, Math.round(+e.target.value)) })} />
              <div className="prop-sec green">Align</div>
              <button className="prop-btn" style={{ width: "100%", marginBottom: 4, display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }} onClick={() => { if (sel) patchSel({ x: Math.round((PAGE_W - sel.width) / 2) }); }}>
                <i className="pi pi-align-center" style={{ fontSize: 11 }} /> Centre on Page
              </button>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4, marginBottom: 8 }}>
                <button className="prop-btn" onClick={() => { if (sel) patchSel({ x: Math.round(40) }); }}>← Left</button>
                <button className="prop-btn" onClick={() => { if (sel) patchSel({ x: Math.round(PAGE_W - sel.width - 40) }); }}>Right →</button>
              </div>
              <div className="prop-sec">Actions</div>
              <button className="prop-btn" style={{ width: "100%", marginBottom: 4 }} onClick={() => onDuplicateEl(sel.id)}>Duplicate</button>
              <button className="prop-btn del" style={{ width: "100%" }} onClick={() => onDeleteEl(sel.id)}>Delete</button>
            </div>
          ) : (
            <div className="props">
              <div className="prop-sec">Position</div>
              <div className="prop-lbl">X (px)</div>
              <input className="prop-inp" type="number" value={Math.round(sel.x)} onChange={(e) => patchSel({ x: Math.round(+e.target.value) })} />
              <div className="prop-lbl">Y (px)</div>
              <input className="prop-inp" type="number" value={Math.round(sel.y)} onChange={(e) => patchSel({ y: Math.round(+e.target.value) })} />
              <div className="prop-lbl">Width (px)</div>
              <input className="prop-inp" type="number" value={Math.round(sel.width)} onChange={(e) => patchSel({ width: Math.max(60, Math.round(+e.target.value)) })} />
              <div className="prop-sec">Typography</div>
              <div className="prop-lbl">Font size (px)</div>
              <input className="prop-inp" type="number" value={(sel as TextElement).fontSize} onChange={(e) => patchSel({ fontSize: +e.target.value })} />
              <div className="prop-lbl">Colour</div>
              <input className="prop-inp" type="color" style={{ height: 30, padding: 2 }} value={(sel as TextElement).color} onChange={(e) => patchSel({ color: e.target.value })} />
              <div className="prop-sec">Page Position</div>
              <button className="prop-btn" style={{ width: "100%", marginBottom: 4, display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }} onClick={() => { if (sel) patchSel({ x: Math.round((PAGE_W - sel.width) / 2) }); }}>
                <i className="pi pi-align-center" style={{ fontSize: 11 }} /> Centre on Page
              </button>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4, marginBottom: 5 }}>
                <button className="prop-btn" onClick={() => { if (sel) patchSel({ x: Math.round(40) }); }}>← Left edge</button>
                <button className="prop-btn" onClick={() => { if (sel) patchSel({ x: Math.round(PAGE_W - sel.width - 40) }); }}>Right edge →</button>
              </div>
              <div className="prop-sec">Actions</div>
              <button className="prop-btn" style={{ width: "100%", marginBottom: 4 }} onClick={() => onDuplicateEl(sel.id)}>Duplicate</button>
              <button className="prop-btn del" style={{ width: "100%" }} onClick={() => onDeleteEl(sel.id)}>Delete</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LetterEditor;